import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Building2, 
  Users, 
  Brain,
  Activity,
  Loader2,
  Key,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { AdminClientsTab, AdminUsersTab, AdminAIConfigTab, AdminAgentLogTab } from '../components/admin';



export default function Admin() {
  const { user: currentUser } = useAuth();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [aiConfig, setAiConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({ 
    name: '', 
    description: '', 
    api_key: '',
    model: 'gpt-4o-mini',
    temperature_min: 0.3,
    temperature_max: 0.8,
    temperature_distribution: 'normal',
    polarization_strength: 0.5,
    reporting_mode: 'peoplelab',
    enabled_modules: ['people_lab'],
    max_populations: '',
    max_stimuli: '',
    max_simulations: '',
    mfa_required: false,
    web_context_enabled: false,
    web_context_api_key: '',
    social_interactions_enabled: false,
    synthetic_attributes_enabled: false,
    monthly_token_budget: '',
  });
  
  // User management state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetUserName, setResetUserName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingWebContext, setTestingWebContext] = useState(false);
  const [webContextStatus, setWebContextStatus] = useState(null);
  
  // New user form
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, usersRes, aiConfigRes] = await Promise.all([
        api.get(`/admin/clients`),
        api.get(`/admin/users`),
        api.get(`/admin/ai-config`)
      ]);
      setClients(clientsRes.data);
      setUsers(usersRes.data);
      setAiConfig(aiConfigRes.data);
    } catch (error) {
      toast.error('Failed to fetch admin data');
    } finally {
      setLoading(false);
    }
  };

  const resetClientForm = () => {
    setClientForm({ 
      name: '', 
      description: '', 
      api_key: '',
      model: 'gpt-4o-mini',
      temperature_min: 0.3,
      temperature_max: 0.8,
      temperature_distribution: 'normal',
      polarization_strength: 0.5,
      reporting_mode: 'peoplelab',
      enabled_modules: ['people_lab'],
      max_populations: '',
      max_stimuli: '',
      max_simulations: '',
      mfa_required: false,
      web_context_enabled: false,
      web_context_api_key: '',
      social_interactions_enabled: false,
      synthetic_attributes_enabled: false,
      monthly_token_budget: '',
      company_context: {
        raw_text: '',
        additional_notes: '',
        is_active: true,
      },
    });
    setEditingClient(null);
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...clientForm };
      // Clean up fields not expected by backend ClientCreate model
      delete payload.web_context_api_key;
      // Convert empty strings to valid defaults for int fields
      payload.max_populations = payload.max_populations ? parseInt(payload.max_populations) : 10;
      payload.max_stimuli = payload.max_stimuli ? parseInt(payload.max_stimuli) : 50;
      payload.max_simulations = payload.max_simulations ? parseInt(payload.max_simulations) : 100;
      payload.monthly_token_budget = payload.monthly_token_budget !== '' && payload.monthly_token_budget != null
        ? parseInt(payload.monthly_token_budget) : null;
      await api.post(`/admin/clients`, payload);
      toast.success('Client created');
      setShowClientForm(false);
      resetClientForm();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : 'Failed to create client';
      toast.error(msg);
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    if (!editingClient) return;
    
    try {
      const payload = {
        ...clientForm,
        max_populations: clientForm.max_populations === '' ? null : parseInt(clientForm.max_populations),
        max_stimuli: clientForm.max_stimuli === '' ? null : parseInt(clientForm.max_stimuli),
        max_simulations: clientForm.max_simulations === '' ? null : parseInt(clientForm.max_simulations),
        monthly_token_budget: clientForm.monthly_token_budget === '' || clientForm.monthly_token_budget == null
          ? null : parseInt(clientForm.monthly_token_budget)
      };
      // Remove web_context_api_key from main update payload
      delete payload.web_context_api_key;
      await api.put(`/admin/clients/${editingClient.id}`, payload);

      // Save web context config separately if changed
      if (clientForm.web_context_enabled !== (editingClient.web_context_enabled || false) || clientForm.web_context_api_key) {
        await api.put(`/admin/clients/${editingClient.id}/web-context`, {
          enabled: clientForm.web_context_enabled,
          provider: 'openai',
          api_key: clientForm.web_context_api_key || null,
        });
      }

      toast.success('Client updated');
      setShowClientForm(false);
      resetClientForm();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : 'Failed to update client';
      toast.error(msg);
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setClientForm({
      name: client.name,
      description: client.description || '',
      api_key: '',
      model: client.model || 'gpt-4o-mini',
      temperature_min: client.temperature_min || 0.3,
      temperature_max: client.temperature_max || 0.8,
      temperature_distribution: client.temperature_distribution || 'normal',
      polarization_strength: client.polarization_strength ?? 0.5,
      reporting_mode: client.reporting_mode || 'peoplelab',
      enabled_modules: client.enabled_modules || (client.reporting_mode === 'marketlab' ? ['market_lab'] : ['people_lab']),
      max_populations: client.max_populations ?? '',
      max_stimuli: client.max_stimuli ?? '',
      max_simulations: client.max_simulations ?? '',
      mfa_required: client.mfa_required || false,
      web_context_enabled: client.web_context_enabled || false,
      web_context_api_key: '',
      social_interactions_enabled: client.social_interactions_enabled || false,
      synthetic_attributes_enabled: client.synthetic_attributes_enabled || false,
      monthly_token_budget: client.monthly_token_budget ?? '',
      company_context: {
        raw_text: client.company_context?.raw_text || '',
        additional_notes: client.company_context?.additional_notes || '',
        is_active: client.company_context?.is_active ?? true,
      },
    });
    setShowClientForm(true);
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure? This will delete all client data.')) return;
    
    try {
      await api.delete(`/admin/clients/${clientId}`);
      toast.success('Client deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const handleTestWebContext = async (clientId) => {
    setTestingWebContext(true);
    setWebContextStatus(null);
    try {
      // If editing, save web context config first
      if (clientId) {
        await api.put(`/admin/clients/${clientId}/web-context`, {
          enabled: clientForm.web_context_enabled,
          provider: 'openai',
          api_key: clientForm.web_context_api_key || null,
        });
      }
      const res = await api.post(`/admin/clients/${clientId}/web-context/test`);
      setWebContextStatus(res.data);
      if (res.data.status === 'connected') {
        toast.success('Web search connection successful');
      } else {
        toast.error(res.data.message || 'Connection failed');
      }
    } catch (error) {
      setWebContextStatus({ status: 'error', message: error.response?.data?.detail || 'Test failed' });
      toast.error('Connection test failed');
    } finally {
      setTestingWebContext(false);
    }
  };

  const handleAssignUser = async (userId, clientId) => {
    try {
      await api.put(`/admin/users/${userId}/assign-client`, { user_id: userId, client_id: clientId });
      toast.success('User assigned to client');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign user');
    }
  };

  const handleToggleCalibrationAccess = async (userId) => {
    try {
      const response = await api.put(`/admin/users/${userId}/calibration-access`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle calibration access');
    }
  };

  const handleUpdateAIConfig = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/ai-config`, aiConfig);
      toast.success('AI configuration updated');
    } catch (error) {
      toast.error('Failed to update AI configuration');
    }
  };

  // User management functions
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.post(`/admin/users?role=${newUserForm.role}`, {
        name: newUserForm.name,
        email: newUserForm.email,
        password: newUserForm.password
      });
      toast.success('User created successfully');
      setUserDialogOpen(false);
      setNewUserForm({ name: '', email: '', password: '', role: 'user' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      fetchData();
      toast.success('Role updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Delete user ${userEmail}? This cannot be undone.`)) return;

    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openResetDialog = (userId, userName) => {
    setResetUserId(userId);
    setResetUserName(userName);
    setNewPassword('');
    setShowPassword(false);
    setResetDialogOpen(true);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResetting(true);
    try {
      await api.put(`/admin/users/${resetUserId}/password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetUserName}`);
      setResetDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const formatLimit = (value) => {
    if (value === null || value === undefined) return '∞';
    return value.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground tracking-tight">Admin Panel</h1>
        <p className="text-muted-foreground mt-1">Manage clients, users, and AI configuration</p>
      </div>

      <Tabs defaultValue="clients" className="space-y-6">
        <TabsList className="bg-secondary">
          <TabsTrigger value="clients" className="data-[state=active]:bg-background">
            <Building2 className="w-4 h-4 mr-2" />
            Clients
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-background">
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="ai-config" className="data-[state=active]:bg-background">
            <Brain className="w-4 h-4 mr-2" />
            Default AI
          </TabsTrigger>
          <TabsTrigger value="agent-log" className="data-[state=active]:bg-background" data-testid="admin-tab-agent-log">
            <Activity className="w-4 h-4 mr-2" />
            Agent Log
          </TabsTrigger>
        </TabsList>

        <AdminClientsTab
          clients={clients}
          clientForm={clientForm}
          editingClient={editingClient}
          showClientForm={showClientForm}
          setShowClientForm={setShowClientForm}
          setClientForm={setClientForm}
          resetClientForm={resetClientForm}
          handleUpdateClient={handleUpdateClient}
          handleCreateClient={handleCreateClient}
          handleEditClient={handleEditClient}
          handleDeleteClient={handleDeleteClient}
          testingWebContext={testingWebContext}
          webContextStatus={webContextStatus}
          handleTestWebContext={handleTestWebContext}
          formatLimit={formatLimit}
        />

        <AdminUsersTab
          users={users}
          clients={clients}
          currentUser={currentUser}
          setUserDialogOpen={setUserDialogOpen}
          handleRoleChange={handleRoleChange}
          handleAssignUser={handleAssignUser}
          handleToggleCalibrationAccess={handleToggleCalibrationAccess}
          openResetDialog={openResetDialog}
          handleDeleteUser={handleDeleteUser}
        />

        <AdminAIConfigTab
          aiConfig={aiConfig}
          setAiConfig={setAiConfig}
          handleUpdateAIConfig={handleUpdateAIConfig}
        />

        <AdminAgentLogTab />
      </Tabs>

      {/* Create User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="new-user-name">Full Name *</Label>
              <Input
                id="new-user-name"
                value={newUserForm.name}
                onChange={(e) => setNewUserForm({...newUserForm, name: e.target.value})}
                placeholder="John Doe"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-user-email">Email *</Label>
              <Input
                id="new-user-email"
                type="email"
                value={newUserForm.email}
                onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                placeholder="john@company.com"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-user-password">Password *</Label>
              <Input
                id="new-user-password"
                type="password"
                value={newUserForm.password}
                onChange={(e) => setNewUserForm({...newUserForm, password: e.target.value})}
                placeholder="••••••••"
                required
                minLength={6}
              />
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newUserForm.role} onValueChange={(v) => setNewUserForm({...newUserForm, role: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saving || !newUserForm.name || !newUserForm.email || !newUserForm.password}
                className="bg-primary hover:bg-primary/90"
              >
                {saving ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{resetUserName}</strong>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePasswordReset} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password *</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Minimum 6 characters</p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setResetDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={resetting || newPassword.length < 6}
                className="bg-primary hover:bg-primary/90"
              >
                {resetting ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
